import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    IconButton,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    FormHelperText,
    TextField,
    Breadcrumbs,
    Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MascaraCnpj } from "../../../utils/MascaraCnpj";
import { MascaraTelefone } from "../../../utils/MascaraTelefone";
import { MascaraCep } from "../../../utils/MascaraCep";

// Ícones
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import HomeIcon from '@mui/icons-material/Home';
import ArtTrackIcon from '@mui/icons-material/ArtTrack';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

// Componentes personalizados
import HeaderDashboard from "../../components/HeaderDashboard";
import ModalNotificacao from "./ModalNotificacao";
import Loading from "../../components/Loading";

// Contexto
import { useUser } from "../../../contexts/UserContext";
import AlertSuccess from "../../components/AlertSuccess";
import AlertError from "../../components/AlertError";

const cardSx = {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    p: { xs: 2, md: 3 },
};

const campoSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
};

const linhaCamposSx = (colunas) => ({
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: colunas },
    gap: 1.5,
    alignItems: "start",
});

const botaoPrimarioSx = {
    textTransform: "none",
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
    backgroundColor: "#ffc845",
    borderRadius: "8px",
    boxShadow: "none",
    py: 1.1,
    "&:hover": { backgroundColor: "#f5b82e", boxShadow: "none" },
};

// Enums do cadastro de empresa da Spedy -> texto exibido
const REGIMES_TRIBUTARIOS = {
    simplesNacional: "Simples Nacional",
    simplesNacionalExcessoSublimite: "Simples Nacional - Excesso de sublimite",
    regimeNormal: "Regime Normal (Lucro Presumido/Real)",
    simplesNacionalMEI: "MEI (Microempreendedor individual)",
};
const REGIMES_ESPECIAIS = {
    noSpecialRegime: "Sem regime especial",
    municipalMicroenterprise: "Microempresa Municipal",
    estimate: "Estimativa",
    societyOfProfessionals: "Sociedade de Profissionais",
    cooperative: "Cooperativa",
    individualMicroenterprise: "Microempresário Individual (MEI)",
    microenterpriseAndSmallBusiness: "Microempresa ou Empresa de Pequeno Porte (ME/EPP)",
    others: "Outros",
};
const RECOLHIMENTOS_SIMPLES = {
    federalAndMunicipalBySimplesNacional: "Tributos federais e municipal pelo Simples Nacional",
    federalBySimplesAndIssqnByNfse: "Federais pelo Simples Nacional e ISSQN pela NFS-e",
    federalAndMunicipalByNfse: "Federais e municipal pela NFS-e",
};

const TIPOS_EMISSAO = {
    annfs: "Emissor Nacional (NFS-e Nacional)",
};
const AMBIENTES = {
    production: "Produção",
    development: "Homologação (testes)",
};

const formatarData = (data) => (data ? new Date(data).toLocaleDateString("pt-BR") : "");

// Definido fora do componente para não remontar os inputs (e perder o foco) a cada render
const SecaoCard = ({ icone, titulo, descricao, children }) => (
    <Box sx={cardSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    "& svg": { fontSize: 20 },
                }}
            >
                {icone}
            </Box>
            <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{titulo}</Typography>
                <Typography sx={{ fontSize: 12, color: "#6b7280" }}>{descricao}</Typography>
            </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
    </Box>
);

// Campo somente leitura: os dados da empresa são cadastrados pela equipe Clarea
const CampoLeitura = ({ label, valor }) => (
    <TextField
        fullWidth
        size="small"
        label={label}
        value={valor || "—"}
        variant="outlined"
        InputProps={{ readOnly: true }}
        InputLabelProps={{ shrink: true }}
        sx={campoSx}
    />
);

const Certificado = () => {
    const { user, fetchUser } = useUser();
    const navigate = useNavigate();

    // Estados gerais
    const [userActive, setUserActive] = useState(true);
    const [openModalNotificacao, setOpenModalNotificacao] = useState(false);
    const [notificacoes, setNotificacoes] = useState([]);
    const [hasCertificado, setHasCertificado] = useState(false);
    const [open, setOpen] = useState(false);
    const [certificadoName, setCertificadoName] = useState(null);
    const [certificadoValidade, setCertificadoValidade] = useState(null);
    const [empresa, setEmpresa] = useState(null);
    const [checkboxFatura, setCheckboxFatura] = useState(false);

    const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
    const [mensagem, setMensagem] = useState("");
    const [openSnackbarError, setOpenSnackbarError] = useState(false);

    // Campos de formulário
    const [email, setEmail] = useState("");
    const [file, setFile] = useState(null);
    const [senha, setSenha] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // Drag & Drop
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const token = localStorage.getItem("token");

    // O certificado só pode ser enviado depois que a equipe Clarea cadastrou a empresa na Spedy
    const podeEnviarCertificado = !!empresa?.idEmpresaSpedy;

    // Validade informada pela Spedy no envio do certificado
    const certificadoVencido = !!certificadoValidade && new Date(certificadoValidade) < new Date();
    const diasParaVencer = certificadoValidade ? Math.ceil((new Date(certificadoValidade) - new Date()) / 86400000) : null;
    const certificadoVencendo = !certificadoVencido && diasParaVencer !== null && diasParaVencer <= 30;
    const certificadoAtivo = hasCertificado && !!certificadoValidade && !certificadoVencido;
    const statusCertificado = !hasCertificado ? "Pendente" : certificadoVencido ? "Vencido" : certificadoVencendo ? "Vencendo" : certificadoValidade ? "Ativo" : "Pendente";
    const emissaoConfigurada = !!user?.empresa?.emissaoConfigurada;

    const mostrarErro = (texto) => {
        setMensagem(texto);
        setOpenSnackbarError(true);
    };

    const mostrarSucesso = (texto) => {
        setMensagem(texto);
        setOpenSnackbarSuccess(true);
    };

    // Busca e-mail do usuário logado
    useEffect(() => {
        if (user?.usuario?.Email) setEmail(user.usuario.Email);
    }, [user]);

    // Busca certificado ativo
    useEffect(() => {
        const getCertificado = async () => {
            setOpen(true);
            try {
                const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/getCertificado`, {
                    headers: { Authorization: token },
                });
                setHasCertificado(data.status === 200);
                if (data.status === 200) {
                    setCertificadoName(data.certificado.arquivoNome);
                    setCertificadoValidade(data.certificado.validade || null);
                }
            } catch (err) {
                console.error("Erro ao buscar certificado:", err);
                setHasCertificado(false);
            } finally {
                setOpen(false);
            }
        };

        getCertificado();
    }, [token]);

    useEffect(() => {
        const getDadosEmpresa = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/getDadosEmpresa`, {
                    headers: { Authorization: token },
                });
                if (response.data.status === 200) {
                    setEmpresa(response.data.data);
                    setCheckboxFatura(response.data.data.gerarFaturas || false);
                } else {
                    setEmpresa(null);
                }
            } catch (err) {
                console.error("Erro ao buscar dados da empresa:", err);
            }
        };
        getDadosEmpresa();
    }, [token]);

    // Funções de drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        validateFile(droppedFile);
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        validateFile(selectedFile);
    };

    const validateFile = (file) => {
        if (file && (file.name.endsWith(".pfx") || file.name.endsWith(".p12"))) {
            setFile(file);
        } else {
            mostrarErro("Por favor, selecione um arquivo .pfx ou .p12 válido.");
        }
    };

    // Controle da visibilidade da senha
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => event.preventDefault();

    // Envio do certificado
    const handleSubmit = async () => {
        if (!podeEnviarCertificado) return mostrarErro("Sua empresa ainda está sendo cadastrada pela equipe Clarea.");
        if (!file) return mostrarErro("Selecione um arquivo de certificado.");
        if (!senha) return mostrarErro("Digite a senha do certificado.");

        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("senha", senha);
        formData.append("email", email);

        try {
            setOpen(true);
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/uploadCertificado`, formData, {
                headers: {
                    Authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });
            if (data.status === 200) {
                mostrarSucesso(data.mensagem || "Certificado atualizado com sucesso!");
                setHasCertificado(true);
                setCertificadoName(data.certificado?.arquivoNome || file.name);
                setCertificadoValidade(data.certificado?.validade || null);
                setFile(null);
                setSenha("");
                // Atualiza o certificado usado na liberação da emissão
                fetchUser();
            } else {
                mostrarErro(data.error || data.mensagem || data.message || "Falha ao atualizar certificado. Verifique os dados e tente novamente.");
            }
        } catch (err) {
            console.error(err);
            mostrarErro(err?.response?.data?.error || "Erro ao enviar o certificado. Tente novamente mais tarde.");
        } finally {
            setOpen(false);
        }
    };

    // Os dados da empresa são somente leitura; aqui o cliente só altera a opção de gerar faturas.
    const handleSalvarFaturas = async () => {
        setOpen(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/salvarDadosEmpresa`, { gerarFaturas: checkboxFatura }, {
                headers: { Authorization: token },
            });
            if (response.data.status === 200) {
                mostrarSucesso(response.data.mensagem || "Preferência salva com sucesso!");
            } else {
                mostrarErro(response.data.message || "Falha ao salvar a preferência.");
            }
        } catch (err) {
            console.error("Erro ao salvar preferência de faturas:", err);
            mostrarErro("Erro ao salvar a preferência. Tente novamente mais tarde.");
        } finally {
            setOpen(false);
        }
    }

    const simplesNacional = String(empresa?.taxRegime || "").startsWith("simplesNacional");

    return (
        <>
            <HeaderDashboard
                userActive={userActive}
                setOpenModalNotificacao={setOpenModalNotificacao}
                notificacoes={notificacoes.length}
            />
            <Box sx={{ pt: "80px", pb: 4, px: { xs: 2, md: "5%" }, minHeight: "100vh" }}>
                <Box
                    sx={{
                        ...cardSx,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 12, "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
                            <Link
                                underline="hover"
                                sx={{ display: 'flex', alignItems: 'center', cursor: "pointer", fontSize: 12, color: "#6b7280" }}
                                onClick={() => navigate("../Dashboard")}
                            >
                                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Dashboard
                            </Link>
                            <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                <ArtTrackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Certificado & Empresa
                            </Typography>
                        </Breadcrumbs>
                        <Typography sx={{ mt: 1, fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: "#111827" }}>
                            Certificado & Empresa
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 14, color: "#4b5563" }}>
                            Envie seu certificado digital e confira os dados da empresa cadastrados pela equipe Clarea.
                        </Typography>
                    </Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("../Dashboard")}
                        sx={{ alignSelf: { xs: "flex-start", md: "center" }, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#374151", backgroundColor: "#f3f4f6", borderRadius: "8px", px: 2, "&:hover": { backgroundColor: "#e5e7eb" } }}
                    >
                        Voltar
                    </Button>
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.3fr) minmax(0, 2.7fr)" },
                        gap: 2,
                        alignItems: "start",
                    }}
                >
                    {/* Certificado digital */}
                    <Box sx={{ ...cardSx, position: { md: "sticky" }, top: { md: 80 } }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Certificado Digital</Typography>
                            <Chip
                                label={statusCertificado}
                                size="small"
                                sx={{
                                    backgroundColor: certificadoAtivo ? (certificadoVencendo ? "#fef3c7" : "#dcfce7") : "#fee2e2",
                                    color: certificadoAtivo ? (certificadoVencendo ? "#b45309" : "#15803d") : "#b91c1c",
                                    fontWeight: 700,
                                    fontSize: 10,
                                    height: 20,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                }}
                            />
                        </Box>

                        {hasCertificado ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, backgroundColor: "#0b243d", borderRadius: "10px", p: 1.5, mb: 2 }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "rgba(255, 255, 255, 0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <VerifiedUserIcon sx={{ fontSize: 20, color: "#ffc845" }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>
                                        {certificadoVencido ? "Certificado vencido" : "Certificado enviado"}
                                    </Typography>
                                    <Typography noWrap title={certificadoName || ""} sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{certificadoName}</Typography>
                                    <Typography sx={{ fontSize: 11, color: certificadoVencido ? "#fca5a5" : certificadoVencendo ? "#fde68a" : "#cbd5e1" }}>
                                        {certificadoValidade
                                            ? `${certificadoVencido ? "Venceu em" : "Válido até"} ${formatarData(certificadoValidade)}${certificadoVencendo ? ` (${diasParaVencer} dia${diasParaVencer === 1 ? "" : "s"})` : ""}`
                                            : "Validade não informada"}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fee2e2", color: "#b91c1c", borderRadius: "10px", p: 1.5, mb: 2 }}>
                                <GppMaybeOutlinedIcon sx={{ fontSize: 20 }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                    Você ainda não enviou o certificado digital A1. Envie seu certificado para liberar a emissão de notas.
                                </Typography>
                            </Box>
                        )}

                        {!podeEnviarCertificado && (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fef3c7", border: "1px solid #fde68a", color: "#b45309", borderRadius: "10px", p: 1.5, mb: 2 }}>
                                <LockOutlinedIcon sx={{ fontSize: 20 }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                    O envio do certificado é liberado depois que a equipe Clarea concluir o cadastro da sua empresa.
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                opacity: podeEnviarCertificado ? 1 : 0.5,
                                pointerEvents: podeEnviarCertificado ? "auto" : "none",
                                userSelect: podeEnviarCertificado ? "auto" : "none",
                            }}
                            aria-disabled={!podeEnviarCertificado}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}>
                                {hasCertificado ? "Atualizar certificado (.pfx ou .p12)" : "Arquivo do certificado (.pfx ou .p12)"}
                            </Typography>
                            <Box
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current.click()}
                                sx={{
                                    border: "2px dashed",
                                    borderColor: isDragging ? "#ffc845" : file ? "#86efac" : "#d1d5db",
                                    borderRadius: "10px",
                                    p: 3,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: isDragging ? "#fffbeb" : file ? "#f0fdf4" : "#f9fafb",
                                    transition: "all 0.2s ease",
                                    "&:hover": { borderColor: "#ffc845" },
                                }}
                            >
                                {file ? (
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: "#16a34a" }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827", wordBreak: "break-all" }}>{file.name}</Typography>
                                        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Clique para trocar o arquivo</Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                                        <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: "#9ca3af" }} />
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                                            Arraste e solte o arquivo aqui
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                                            ou <Box component="span" sx={{ fontWeight: 700, color: "#111827", textDecoration: "underline" }}>procure no computador</Box>
                                        </Typography>
                                    </Box>
                                )}
                                <input
                                    type="file"
                                    accept=".pfx,.p12"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    style={{ display: "none" }}
                                />
                            </Box>

                            {/* Senha do certificado */}
                            <FormControl sx={{ mt: 2, ...campoSx }} fullWidth size="small" variant="outlined" autoComplete="off">
                                <InputLabel htmlFor="outlined-adornment-password">Senha do Certificado</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-password"
                                    name="fakepassword"
                                    value={senha}
                                    onChange={(e) => setSenha(e.target.value)}
                                    placeholder="Digite a senha do seu certificado"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                aria-label="toggle password visibility"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Senha do Certificado"
                                />
                                <FormHelperText>
                                    A senha é necessária para validar o arquivo do certificado.
                                </FormHelperText>
                            </FormControl>

                            {/* E-mail */}
                            <FormControl sx={{ mt: 2, ...campoSx }} fullWidth size="small" variant="outlined">
                                <InputLabel htmlFor="email-notificacao">E-mail para notificação</InputLabel>
                                <OutlinedInput
                                    id="email-notificacao"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Digite seu e-mail"
                                    type="email"
                                    autoComplete="off"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <EmailIcon sx={{ color: "#9ca3af" }} />
                                        </InputAdornment>
                                    }
                                    label="E-mail para notificação"
                                />
                                <FormHelperText>
                                    Enviaremos notificações para este e-mail quando o certificado estiver próximo do vencimento.
                                </FormHelperText>
                            </FormControl>

                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<CloudUploadOutlinedIcon />}
                                sx={{ ...botaoPrimarioSx, mt: 3 }}
                                onClick={handleSubmit}
                                disabled={!podeEnviarCertificado}
                            >
                                {hasCertificado ? "Atualizar Certificado" : "Enviar Certificado"}
                            </Button>
                        </Box>
                    </Box>

                    {/* Dados da empresa (somente leitura) */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {!empresa ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, backgroundColor: "#fef3c7", border: "1px solid #fde68a", borderRadius: "12px", px: 2, py: 1.5, color: "#b45309" }}>
                                <LockOutlinedIcon sx={{ fontSize: 20 }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    Sua empresa ainda não foi cadastrada. A equipe Clarea fará o cadastro e os dados aparecerão aqui.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <SecaoCard icone={<BusinessIcon />} titulo="Identificação" descricao="Dados cadastrados pela equipe Clarea. Para alterar, fale com o suporte.">
                                    <CampoLeitura label="CNPJ" valor={MascaraCnpj(empresa.cnpj || "")} />
                                    <Box sx={linhaCamposSx("1fr 1fr")}>
                                        <CampoLeitura label="Inscrição estadual" valor={empresa.inscricaoEstadual} />
                                        <CampoLeitura label="Inscrição municipal" valor={empresa.inscricaoMunicipal} />
                                    </Box>
                                    <CampoLeitura label="Razão Social" valor={empresa.razaoSocial} />
                                    <CampoLeitura label="Nome Fantasia" valor={empresa.nomeFantasia} />
                                </SecaoCard>

                                <SecaoCard icone={<ReceiptLongOutlinedIcon />} titulo="Emissão de notas" descricao="Configuração da NFS-e feita pela equipe Clarea">
                                    <Box sx={linhaCamposSx("1fr 1fr")}>
                                        <CampoLeitura label="Situação" valor={emissaoConfigurada ? "Emissão habilitada" : "Em configuração pela equipe Clarea"} />
                                        <CampoLeitura label="Tipo de emissão" valor={TIPOS_EMISSAO[empresa.issueType] || empresa.issueType} />
                                    </Box>
                                    <Box sx={linhaCamposSx("1fr 1fr 1fr")}>
                                        <CampoLeitura label="Série" valor={empresa.series} />
                                        <CampoLeitura label="Próximo número da nota" valor={empresa.nextNumber} />
                                        <CampoLeitura label="Ambiente" valor={AMBIENTES[empresa.environmentType] || empresa.environmentType} />
                                    </Box>
                                </SecaoCard>

                                <SecaoCard icone={<AccountBalanceOutlinedIcon />} titulo="Tributação" descricao="Enquadramento fiscal da empresa">
                                    <Box sx={linhaCamposSx(simplesNacional ? "1fr 1fr 1fr" : "1fr 1fr")}>
                                        <CampoLeitura label="Regime tributário" valor={REGIMES_TRIBUTARIOS[empresa.taxRegime]} />
                                        <CampoLeitura label="Regime tributário especial" valor={REGIMES_ESPECIAIS[empresa.specialTaxRegime]} />
                                        {simplesNacional && (
                                            <CampoLeitura label="Recolhimento no Simples Nacional" valor={RECOLHIMENTOS_SIMPLES[empresa.simplesNacionalTaxRegime]} />
                                        )}
                                    </Box>
                                </SecaoCard>

                                <SecaoCard icone={<LocationOnOutlinedIcon />} titulo="Endereço e contato" descricao="Endereço fiscal e canais de contato da empresa">
                                    <Box sx={linhaCamposSx("1fr 2fr 0.7fr")}>
                                        <CampoLeitura label="CEP" valor={MascaraCep(empresa.cep || "")} />
                                        <CampoLeitura label="Endereço" valor={empresa.endereco} />
                                        <CampoLeitura label="Número" valor={empresa.numero} />
                                    </Box>
                                    <Box sx={linhaCamposSx("1.5fr 1.5fr 0.7fr")}>
                                        <CampoLeitura label="Bairro" valor={empresa.bairro} />
                                        <CampoLeitura label="Cidade" valor={empresa.cidade} />
                                        <CampoLeitura label="Estado" valor={empresa.estado ? String(empresa.estado).toUpperCase() : ""} />
                                    </Box>
                                    <Box sx={linhaCamposSx("1.5fr 1fr 1fr")}>
                                        <CampoLeitura label="E-mail da empresa" valor={empresa.email} />
                                        <CampoLeitura label="Telefone" valor={MascaraTelefone(empresa.telefone || "")} />
                                        <CampoLeitura label="Celular" valor={MascaraTelefone(empresa.celular || "")} />
                                    </Box>
                                </SecaoCard>

                                <Box
                                    sx={{
                                        ...cardSx,
                                        p: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1.5,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={checkboxFatura} onChange={e => setCheckboxFatura(e.target.checked)} sx={{ "&.Mui-checked": { color: "#0b243d" } }} />}
                                        label="Gerar as faturas para este CNPJ?"
                                        sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: "#374151" } }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        sx={{ ...botaoPrimarioSx, px: 3, width: { xs: "100%", sm: "auto" } }}
                                        onClick={handleSalvarFaturas}
                                    >
                                        Salvar preferência
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
            <ModalNotificacao
                openModalNotificacao={openModalNotificacao}
                onClose={() => setOpenModalNotificacao(false)}
                notificacoes={notificacoes}
            />
            <Loading open={open} />
            <AlertSuccess setOpenSnackbarSuccess={setOpenSnackbarSuccess} openSnackbarSuccess={openSnackbarSuccess} mensagem={mensagem} />
            <AlertError setOpenSnackbarError={setOpenSnackbarError} openSnackbarError={openSnackbarError} mensagem={mensagem} />
        </>
    );
};

export default Certificado;
